import { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "@localization/useTranslation";
import { EntityType } from "@common/Common.types";
import TopBarTile from "@common/components/entityDetailTopBar/TopBarTile";
import TopBarNumberTile from "@common/components/entityDetailTopBar/TopBarNumberTile";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import RiskAreaRiskLines from "./RiskAreaRiskLines";
import { useRiskAreaRisksTile } from "./RiskAreaRisksTile.hooks";
import RiskAreaRisksDialog from "./RiskAreaRisksDialog";

export interface IRiskAreaRisksTile {
    entityType: EntityType;
    entityId: string;
}

// The entity top-bar RISK tile for Enterprise Risk Management. A single scored area renders inline as
// "area name: chip"; several collapse into a count that opens a dialog listing each area with its chip.
// The tile is always shown, blank like every other empty tile when the entity has no scored area. Callers
// already gate rendering on Enterprise Risk Management access, so the tile just fetches.
function RiskAreaRisksTile({ entityType, entityId }: IRiskAreaRisksTile) {
    const { translateString } = useTranslation();
    const { riskAreas, isLoading } = useRiskAreaRisksTile(entityType, entityId);
    const [showDialog, setShowDialog] = useState(false);

    if (isLoading) {
        return <TopBarTextTile label={translateString("risk")} value="" isLoading />;
    }

    if (riskAreas.length === 0) {
        return <TopBarTextTile label={translateString("risk")} value={undefined} />;
    }

    if (riskAreas.length === 1) {
        return (
            <TopBarTile label={translateString("risk")}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <RiskAreaRiskLines riskAreas={riskAreas} entityType={entityType} entityId={entityId} />
                </Box>
            </TopBarTile>
        );
    }

    return (
        <>
            <TopBarNumberTile label={translateString("risk")} value={riskAreas.length} onClick={() => setShowDialog(true)} />
            {showDialog && <RiskAreaRisksDialog riskAreas={riskAreas} entityType={entityType} entityId={entityId} onClose={() => setShowDialog(false)} />}
        </>
    );
}

export default RiskAreaRisksTile;
